import { useState, type FormEvent } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { SiteHeader } from '../components/SiteHeader';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { trackEvent } from '../lib/analytics';

const CONTACT_API = 'https://tylerwrenn.com/contactapi/index.php';
const RECAPTCHA_SITE_KEY = '6LcNWb8UAAAAACQaA-aOwVV2GtmIuHnRvhqE9B7O';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldName = 'firstname' | 'lastname' | 'email' | 'subject' | 'message';
type Fields = Record<FieldName, string>;
type Status = { kind: 'success' | 'danger'; message: string } | null;

const EMPTY: Fields = { firstname: '', lastname: '', email: '', subject: '', message: '' };
const FIELD_ORDER: FieldName[] = ['firstname', 'lastname', 'email', 'subject', 'message'];

function fieldError(name: FieldName, value: string): string | null {
  if (value.trim() === '') {
    return name === 'email' ? 'Please enter your email.' : `Please enter your ${labelFor(name).toLowerCase()}.`;
  }
  if (name === 'email' && !EMAIL_RE.test(value)) {
    return 'Please enter your email.';
  }
  return null;
}

function labelFor(name: FieldName): string {
  switch (name) {
    case 'firstname':
      return 'First Name';
    case 'lastname':
      return 'Last Name';
    case 'email':
      return 'Email';
    case 'subject':
      return 'Subject';
    case 'message':
      return 'Message';
  }
}

export function Inquiries() {
  useDocumentMeta({ title: 'Inquiries | Tyler Wrenn' });

  const [fields, setFields] = useState<Fields>(EMPTY);
  const [touched, setTouched] = useState<Record<FieldName, boolean>>({
    firstname: false,
    lastname: false,
    email: false,
    subject: false,
    message: false,
  });
  const [captcha, setCaptcha] = useState('');
  const [status, setStatus] = useState<Status>(null);
  const [submitting, setSubmitting] = useState(false);

  const setField = (name: FieldName, value: string) => setFields((prev) => ({ ...prev, [name]: value }));
  const markTouched = (name: FieldName) => setTouched((prev) => ({ ...prev, [name]: true }));

  const showError = (name: FieldName): string | null =>
    touched[name] ? fieldError(name, fields[name]) : null;

  const isValid = FIELD_ORDER.every((name) => fieldError(name, fields[name]) === null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTouched({ firstname: true, lastname: true, email: true, subject: true, message: true });

    if (!isValid) {
      const firstBad = FIELD_ORDER.find((name) => fieldError(name, fields[name]) !== null);
      if (firstBad === 'firstname') {
        setStatus({ kind: 'danger', message: 'You must enter a first name.' });
      }
      return;
    }

    const payload = {
      firstname: fields.firstname,
      lastname: fields.lastname,
      email: fields.email,
      subject: fields.subject,
      message: fields.message,
      gRecaptchaResponse: captcha,
    };

    setSubmitting(true);
    try {
      const response = await fetch(CONTACT_API, { method: 'POST', body: JSON.stringify(payload) });
      const json = (await response.json().catch(() => null)) as { status?: string } | null;
      if (json?.status === 'success') {
        trackEvent('submit_success', 'user_inquiry_form', 'inquiries_page');
        setStatus({ kind: 'success', message: 'Message was sent successfully!' });
        setFields(EMPTY);
      } else {
        trackEvent('submit_failure', 'user_inquiry_form', 'inquiries_page');
        setStatus({ kind: 'danger', message: 'There was an error. Please try filling out the form again.' });
      }
    } catch {
      trackEvent('submit_failure', 'user_inquiry_form', 'inquiries_page');
      setStatus({ kind: 'danger', message: 'There was an error. Please try filling out the form again.' });
    } finally {
      setSubmitting(false);
    }
  }

  const renderField = (name: FieldName, extra?: { type?: string; textarea?: boolean; autoComplete?: string }) => {
    const error = showError(name);
    const common = {
      id: name,
      name,
      className: `form-control${error ? ' is-invalid' : ''}`,
      placeholder: labelFor(name),
      value: fields[name],
      onBlur: () => markTouched(name),
      'aria-invalid': error ? true : undefined,
    };
    return (
      <div className="inquiries__field">
        <label htmlFor={name}>{labelFor(name)}</label>
        {extra?.textarea ? (
          <textarea
            {...common}
            rows={5}
            onChange={(e) => setField(name, e.target.value)}
          />
        ) : (
          <input
            {...common}
            type={extra?.type ?? 'text'}
            maxLength={name === 'firstname' || name === 'lastname' ? 50 : undefined}
            autoComplete={extra?.autoComplete}
            onChange={(e) => setField(name, e.target.value)}
          />
        )}
        {error && (
          <div className="invalid-feedback">
            <p>{error}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <main>
      <SiteHeader />

      <div className="inquiries">
        <div className="inquiries__card">
          <h2 className="inquiries__heading">Inquiries</h2>
          <p className="inquiries__intro">Please fill out this form to inquire.</p>

          <form className="inquiries__form" onSubmit={onSubmit} noValidate>
            <div className="inquiries__row">
              {renderField('firstname', { autoComplete: 'given-name' })}
              {renderField('lastname', { autoComplete: 'family-name' })}
            </div>

            {renderField('email', { type: 'email', autoComplete: 'email' })}
            {renderField('subject')}
            {renderField('message', { textarea: true })}

            <ReCAPTCHA
              className="d-block mt-3"
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(token) => setCaptcha(token ?? '')}
            />

            <div
              id="status"
              role="status"
              aria-live="polite"
              className={status ? `alert alert-${status.kind}` : undefined}
            >
              {status?.message}
            </div>

            <input
              type="submit"
              className="inquiries__submit border-0"
              value={submitting ? 'Sending…' : 'Submit'}
              disabled={submitting}
            />
          </form>
        </div>

        <footer className="inquiries__footer text-center">
          <hr className="border-footer" />
          <p className="small">Designed by Tyler Wrenn</p>
        </footer>
      </div>
    </main>
  );
}
